import { createRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { layoutRoute } from "../$layoutRoute";
import { AccountNavigation } from "./components/account-navigation";
import { AccountMobileNavigation } from "./components/account-mobile-navigation";

export const accountRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "account",
  component: AccountPage
});

/**
 * Main account layout component with two-column structure
 *
 * Features:
 * - Responsive two-column layout (sidebar + content)
 * - Proper focus management for accessibility
 * - Screen reader announcements for route changes
 * - Semantic HTML structure
 * - Mobile horizontal tabs navigation
 */
function AccountPage() {
  const router = useRouterState();
  const mainRef = useRef<HTMLElement>(null);

  // Announce page changes to screen readers and manage focus
  useEffect(() => {
    // Focus main content area when route changes
    mainRef.current?.focus();
  }, [router.location.pathname]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      {/* Page Header */}
      <header className="mb-6">
        <h1
          className="text-2xl font-bold text-gray-900 dark:text-white"
          id="account-page-title"
        >
          Account Settings
        </h1>
        <p
          className="mt-1 text-sm text-gray-600 dark:text-gray-400"
          id="account-page-description"
        >
          Manage your salon&apos;s profile and preferences
        </p>
      </header>

      {/* Mobile Navigation - Horizontal Tabs (visible on < lg) */}
      <div className="mb-6 lg:hidden">
        <AccountMobileNavigation />
      </div>

      {/* Two-Column Layout */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Left Sidebar - Navigation (hidden on mobile) */}
        <aside
          className="hidden w-56 flex-shrink-0 lg:block"
          aria-label="Account navigation"
        >
          <AccountNavigation />
        </aside>

        {/* Right Content - Dynamic Pages */}
        <main
          ref={mainRef}
          className="min-w-0 flex-1"
          id="main-content"
          tabIndex={-1}
          aria-labelledby="account-page-title"
          aria-describedby="account-page-description"
        >
          {/* Content Card */}
          <div className="border-border bg-card rounded-lg border">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
